
import { NoseurObject } from "@rivtn/noseur";
import { BaseService, SanitizedResponse } from "../BaseService";

export enum ProgramMetric {

    RANK = "RANK",
    KILLS = "KILLS",
    LEVEL = "LEVEL",

}

export enum TeamTypes {

    ALL = "ALL",
    DUO = "DUO",
    SOLO = "SOLO",
    TRIO = "TRIO",
    SQUAD = "SQUAD",
    SEXTET = "SEXTET",

}

export const TeamTypesValue: NoseurObject<number> = {

    [TeamTypes.ALL]: -1,
    [TeamTypes.SOLO]: 1,
    [TeamTypes.DUO]: 2,
    [TeamTypes.TRIO]: 3,
    [TeamTypes.SQUAD]: 4,
    [TeamTypes.SEXTET]: 6,

}

export enum ProgramPlatforms {

    PC = "PC",
    ALL = "ALL",
    WII = "WII",
    MAC = "MAC",
    PSP = "PSP",
    SWITCH = "SWITCH",
    MOBILE = "MOBILE",
    THREEDS = "THREEDS",

}

export interface IProgram {
    name: string;
    approved: boolean;
    physical: boolean;
    copyright: string;
    full_name: string;
    short_name: string;
    features: string[];
    platforms: string[];
    logo_urls: string[];
    external_id: string;
    player_ids: string[];
    banner_urls: string[];
    team_types: TeamTypes[];
    metrics: ProgramMetric[];
    no_of_tournaments: number;
    extra_data: NoseurObject<any>;
    past_tournaments_count: number;
    ongoing_tournaments_count: number;
    upcoming_tournaments_count: number;
}

export class ProgramService extends BaseService {

    protected static instance: ProgramService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!ProgramService.instance) ProgramService.instance = new ProgramService();
        return ProgramService.instance;
    }

    queryPrograms(params: NoseurObject<string | number> = { page: 1, size: 10 }): Promise<any> {
        return this.report(this.transport.get("/v1/program", { query: this.normalizeParams(params) }));
    }

    createProgram(program: Partial<IProgram>): Promise<any> {
        return this.report(this.transport.post("/v1/program", program));
    }

    updateProgram(program: Partial<IProgram>): Promise<any> {
        return this.report(this.transport.patch(`/v1/program/${program.external_id}`, program));
    }

    static TEST_PROGRAMS: IProgram[] = [
        {
            name: "All",
            external_id: "1",
            copyright: "Respective Publisher",
            upcoming_tournaments_count: 350,
            logo_urls: [
                "https://1000logos.net/wp-content/uploads/2022/05/Best-Android-Games-Logos.png"
            ],
        },
        {
            name: "Fortnite",
            external_id: "2",
            copyright: "EA Games",
            upcoming_tournaments_count: 33,
            logo_urls: [
                "https://upload.wikimedia.org/wikipedia/commons/7/7c/Fortnite_F_lettermark_logo.png"
            ],
        },
        {
            external_id: "3",
            name: "Rocket League",
            copyright: "EA Games",
            pending_approval: true,
            upcoming_tournaments_count: 12,
            logo_urls: [
                "https://www.nicepng.com/png/detail/402-4027190_https-i-imgur-com-lenktov-rocket-league-logo.png"
            ],
        },
        {
            external_id: "4",
            name: "Football",
            copyright: "FIFA",
            upcoming_tournaments_count: 9,
            logo_urls: [
                "https://t4.ftcdn.net/jpg/02/11/51/53/360_F_211515361_bnIbyKadClzn3hJT0zCPPuPApcG7k3lC.jpg"
            ],
        },
        {
            external_id: "5",
            copyright: "Activision",
            name: "Call Of Duty: Warzone",
            upcoming_tournaments_count: 28,
            logo_urls: [
                "https://logowik.com/content/uploads/images/call-of-duty-warzone-game2635.jpg",
                "https://upload.wikimedia.org/wikipedia/en/1/13/Paladins_%28video_game%29.jpg",
                "https://upload.wikimedia.org/wikipedia/en/d/db/Apex_legends_cover.jpg",
            ],
            banner_urls: [
                "https://blz-contentstack-images.akamaized.net/v3/assets/bltf408a0557f4e4998/blt476e411d696dfb08/67e603a1d66ebd1287f688e5/BO6_S3_WZ_X1-Garrison-App-1920x1080.png?imwidth=1920&imdensity=2.625",
                "https://gaming-cdn.com/images/products/1843/orig/assassin-s-creed-origins-pc-game-ubisoft-connect-europe-cover.jpg?v=1731486408",
                "https://cdn1.epicgames.com/offer/badb0ee71b474ed591ec43212547cfc8/Paladins_EGS_BaseGame_2560x1440_2560x1440-99d0ed45c628c499ad600a52217b4123",
            ],
        },
        {
            name: "Bowling",
            external_id: "6",
            copyright: "BowlNow",
            upcoming_tournaments_count: 1,
            logo_urls: [
                "https://www.shutterstock.com/image-vector/hot-bowling-ball-fire-logo-600nw-2229327253.jpg"
            ],
        },
        {
            name: "Debates",
            external_id: "7",
            pending_approval: true,
            copyright: "Parliament",
            upcoming_tournaments_count: 1,
            logo_urls: [
                "https://www.shutterstock.com/image-vector/podium-rostrum-icon-logo-design-260nw-1845473401.jpg"
            ],
        },
        {
            name: "Robotics",
            external_id: "8",
            pending_approval: true,
            copyright: "Rivtn Lab",
            upcoming_tournaments_count: 1,
            logo_urls: [
                "https://img.freepik.com/premium-vector/robotic-logo-design_166235-192.jpg"
            ],
        },
        {
            name: "Swimming",
            external_id: "9",
            copyright: "Olympic",
            upcoming_tournaments_count: 1,
            logo_urls: [
                "https://www.freeiconspng.com/uploads/swim-logo-icon-17.png"
            ],
        },
        {
            name: "Warhammer 40K",
            external_id: "10",
            copyright: "HammerTech",
            upcoming_tournaments_count: 1,
            logo_urls: [
                "https://logos-world.net/wp-content/uploads/2023/05/Warhammer-Logo.png"
            ],
        },
        {
            name: "Dance",
            external_id: "11",
            copyright: "GDB Dance Creq",
            upcoming_tournaments_count: 1,
            logo_urls: [
                "https://www.shutterstock.com/image-vector/silhouette-dancing-couple-isolated-on-600nw-172487012.jpg"
            ],
        },
        {
            name: "Break Dance",
            external_id: "12",
            copyright: "RayGun",
            upcoming_tournaments_count: 1,
            logo_urls: [
                "https://www.shutterstock.com/image-vector/man-dancing-break-dance-street-600nw-2024798537.jpg"
            ],
        },
        {
            name: "FC 25",
            external_id: "13",
            copyright: "EA Games",
            pending_approval: true,
            upcoming_tournaments_count: 27,
            logo_urls: [
                "https://cdn.gameboost.com/games/logos/fc-25.png"
            ],
        },
        {
            name: "PUBG: Mobile",
            external_id: "14",
            copyright: "Tencent",
            upcoming_tournaments_count: 5,
            logo_urls: [
                "https://play-lh.googleusercontent.com/vkziTNqdZU0su1t-AdjiCyre0SSnHTQJmznppAfbGTtj98yzttsChqcvUOihEkXDsoNZ"
            ],
        },
        {
            name: "Need For Speed: Payback",
            external_id: "15",
            copyright: "EA Sport",
            upcoming_tournaments_count: 25,
            logo_urls: [
                "https://play-lh.googleusercontent.com/rDAmDCGhyX7FcXYJcR0QrcO04uWgdiQaiWw6oA3T93oK4Xq8qOof0-J_qtw2XFYpKg"
            ],
        },
        {
            name: "Warhammer Games",
            external_id: "16",
            copyright: "Blizzard",
            upcoming_tournaments_count: 4,
            logo_urls: [
                "https://upload.wikimedia.org/wikipedia/en/4/42/Warhammer_40%2C000_Space_Marine_2_cover_art.jpg"
            ],
        },
        {
            name: "Apex Legends",
            external_id: "17",
            copyright: "Blizzard",
            upcoming_tournaments_count: 91,
            logo_urls: [
                "https://upload.wikimedia.org/wikipedia/en/d/db/Apex_legends_cover.jpg"
            ],
        },
        {
            name: "Clash Royale",
            external_id: "18",
            pending_approval: true,
            copyright: "Mobi Games",
            upcoming_tournaments_count: 62,
            logo_urls: [
                "https://play-lh.googleusercontent.com/gnSC6s8-6Tjc4uhvDW7nfrSJxpbhllzYhgX8y374N1LYvWBStn2YhozS9XXaz1T_Pi2q"
            ],
        },
        {
            external_id: "19",
            copyright: "Activision",
            upcoming_tournaments_count: 9,
            name: "Assassin Creed: Origin",
            logo_urls: [
                "https://m.media-amazon.com/images/I/A1Lc+0V8SfL._UF1000,1000_QL80_.jpg"
            ],
        },
        {
            external_id: "20",
            copyright: "SEGA",
            pending_approval: true,
            upcoming_tournaments_count: 0,
            name: "Paladins",
            logo_urls: [
                "https://upload.wikimedia.org/wikipedia/en/1/13/Paladins_%28video_game%29.jpg"
            ],
        },
        {
            external_id: "21",
            copyright: "Activision",
            upcoming_tournaments_count: 0,
            name: "PUBG",
            logo_urls: [
                "https://tse4.mm.bing.net/th/id/OIP.wHxHd6Xp94K9Kmn143Nb4gHaHa?rs=1&pid=ImgDetMain&o=7&rm=3"
            ],
        },
        {
            external_id: "22",
            copyright: "SEGA",
            upcoming_tournaments_count: 0,
            name: "Super Mario",
            logo_urls: [
                "https://mediaproxy.tvtropes.org/width/1200/https://static.tvtropes.org/pmwiki/pub/images/super_mario_5.png"
            ],
        },
    ] as any as IProgram[];

}
